import { Autocomplete, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import useGetBanks from "@/hooks/requests/t2Pay/use-get-banks";
import { BankAccountInput } from "@/interfaces/talk2pay.iterfaces";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";

import { ACCOUNT_TYPE_OPTIONS, BANKS_OPTIONS } from "../../../../../constants";
import { buildBankAccountFormSchema, buildBankAccountInitialData } from "../../../../../form-data";

type Props = {
  messages: T2PayHomeMessages;
  onSubmit: (values: BankAccountInput) => void;
  onCancel: () => void;
};

const BankAccountForm = ({ messages, onSubmit, onCancel }: Props) => {
  const accountTypeOptions = ACCOUNT_TYPE_OPTIONS(messages);
  const { data, isLoading: isLoadingBanks } = useGetBanks();

  return (
    <Formik
      initialValues={buildBankAccountInitialData()}
      validationSchema={buildBankAccountFormSchema(messages)}
      onSubmit={onSubmit}
    >
      {({ values, errors, handleChange, setFieldValue, submitForm }) => (
        <MDBox p={3}>
          <MDTypography variant="h5" fontWeight="bold">
            {messages.formTitle}
          </MDTypography>
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormField
                  name="name"
                  label={messages.nicknameLabel}
                  value={values.name}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.name}
                  required
                  variant="standard"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete
                  fullWidth
                  options={BANKS_OPTIONS(data?.items)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={
                    BANKS_OPTIONS(data?.items).find((option) => option.value === values.bankName) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setFieldValue("bankName", newValue.value);
                      setFieldValue("bankCode", newValue.label.split(" - ")[0]);
                    } else {
                      setFieldValue("bankName", "");
                      setFieldValue("bankCode", "");
                    }
                  }}
                  loadingText={messages.loadingText}
                  noOptionsText={messages.noOptionsItems}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label={messages.bankLabel}
                      variant="standard"
                      error={!!errors.bankName}
                      helperText={errors.bankName}
                      disabled={isLoadingBanks}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>{isLoadingBanks && <CircularProgress color="inherit" size={20} />}</>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 250,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete
                  fullWidth
                  options={accountTypeOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={accountTypeOptions.find((option) => option.value === values.type) || null}
                  onChange={(_, newValue) => {
                    setFieldValue("type", newValue ? newValue.value : "");
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label={messages.accountTypeLabel}
                      variant="standard"
                      error={!!errors.type}
                      helperText={errors.type}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormField
                  name="agency"
                  label={messages.agencyLabel}
                  value={values.agency}
                  onChange={handleChange}
                  placeholder="0000"
                  fullWidth
                  error={!!errors.agency}
                  variant="standard"
                  required
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormField
                  name="number"
                  label={messages.accountNumberLabel}
                  value={values.number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const number = e.target.value;
                    const digit = number.slice(-1);
                    setFieldValue("number", number);
                    setFieldValue("digit", digit);
                  }}
                  fullWidth
                  error={!!errors.number}
                  required
                  variant="standard"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
            </Grid>

            <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton variant="gradient" color="light" onClick={onCancel}>
                {messages.cancelButton}
              </MDButton>
              <MDButton variant="gradient" color="dark" onClick={submitForm}>
                {messages.updateButton}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </Formik>
  );
};

export default BankAccountForm;
